import { ChangeDetectionStrategy, Component, input } from '@angular/core';

export type BadgeColor = 'primary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';

/**
 * Badge Component
 *
 * A small status indicator with color variants and optional accent border.
 * Used for labels, statuses, and categorization.
 *
 * @example
 * ```html
 * <zag-badge color="success">Active</zag-badge>
 * <zag-badge color="warning" [accent]="true">Pending</zag-badge>
 * ```
 */
@Component({
  selector: 'zag-badge',
  template: `<ng-content></ng-content>`,
  styleUrl: './badge.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.zag-badge]': 'true',
    '[class.zag-badge--primary]': 'color() === "primary"',
    '[class.zag-badge--success]': 'color() === "success"',
    '[class.zag-badge--warning]': 'color() === "warning"',
    '[class.zag-badge--error]': 'color() === "error"',
    '[class.zag-badge--info]': 'color() === "info"',
    '[class.zag-badge--neutral]': 'color() === "neutral"',
    '[class.zag-badge--accent]': 'accent()',
    '[class.zag-badge--solid]': 'solid()',
  },
})
export class Badge {
  /** Color variant */
  readonly color = input<BadgeColor>('primary');

  /** Show left border accent */
  readonly accent = input<boolean>(false);

  /** Use a fully opaque solid background instead of the default translucent tint */
  readonly solid = input<boolean>(false);
}
